import { useHTranslate } from './use-translate';

/**
 * Composable for form validation rules
 * @returns {Object} Object containing all validation rule functions
 */
export function useFormRules() {
  // Hub identifier: optional `.lowercase()` coerce + `/^\S*$/` (no whitespace).
  const keyRules = (isRequired = true) => {
    const rules: Array<(value: any) => string | true> = [];

    if (isRequired) {
      rules.push((value) => !!value || useHTranslate('key_required'));
    }

    rules.push((value) => {
      if (!value) return true;
      return /^\S*$/.test(value) || useHTranslate('key_format');
    });

    return rules;
  };

  // Hub slug: same `/^\S*$/` as identifiers (API `.lowercase()` coerces).
  const slugRules = (isRequired = true) => {
    const rules: Array<(value: any) => string | true> = [];

    if (isRequired) {
      rules.push((value) => !!value || useHTranslate('slug_required'));
    }

    rules.push((value) => {
      if (!value) return true;
      return /^\S*$/.test(value) || useHTranslate('slug_format');
    });

    return rules;
  };

  // Settings/strings key: `/^[a-z0-9_]*$/`. Strings API `.lowercase()` coerces so uppercase may be allowed.
  const stringKeyRules = (isEditing = false, options: { allowUppercase?: boolean } = {}) => {
    if (isEditing) return [];
    const pattern = options.allowUppercase ? /^[a-zA-Z0-9_]*$/ : /^[a-z0-9_]*$/;
    return [
      (value) => !!value || useHTranslate('key_required'),
      (value) => (value && pattern.test(value)) || useHTranslate('string_key_format'),
    ];
  };

  // Translation locale keys: API object pattern `/^/` (any key). Required when the row exists.
  const localeKeyRules = [(value) => !!value || useHTranslate('translation_locale_required')];

  // General minimum length validation rule
  const minLengthRules = (minLength = 2) => [
    (value) => !value || value.length >= minLength || useHTranslate('field_min_length', { min: minLength }),
  ];

  // General required validation rule with custom message text
  // Properly handles arrays, strings, numbers, and booleans
  const requiredRules = (message?: string) => [
    (value) => {
      // Null or undefined is invalid
      if (value === null || value === undefined) {
        return message || useHTranslate('field_required');
      }
      // Empty string is invalid
      if (typeof value === 'string' && value.trim() === '') {
        return message || useHTranslate('field_required');
      }
      // Empty array is invalid
      if (Array.isArray(value) && value.length === 0) {
        return message || useHTranslate('field_required');
      }
      // Numbers (including 0) and booleans (including false) are valid
      return true;
    },
  ];

  // Name validation rules — Joi.string().required(), no min length
  const nameRules = [(value) => !!value || useHTranslate('name_required')];

  // ID validation rules (lowercase alphanumeric with hyphens and underscores)
  const idRules = (message?: string) => [(value) => !value || /^[a-z0-9_-]*$/.test(value) || message || useHTranslate('action_id_format')];

  // Hub `Utils.emailValidationRegex()` (forms notification to/cc). TLD 2–10 letters or IP-ish.
  // eslint-disable-next-line security/detect-unsafe-regex -- must match Hub Utils.emailValidationRegex(); nested domain group is bounded by @ and TLD
  const hubEmailPattern = /^([\w-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,10}|[0-9]{1,3})(\]?)$/;

  // Email validation rules. Format defaults on (optional empty still passes). `format: 'hub'` uses API regex.
  const emailRules = (isRequired: boolean, options: { format?: boolean | 'hub' } = {}) => {
    const rules: Array<(value: any) => string | true> = [];
    const format = options.format ?? true;

    if (isRequired) {
      rules.push((value) => !!value || useHTranslate('email_required'));
    }

    if (format) {
      const pattern = format === 'hub' ? hubEmailPattern : /.+@.+\..+/;
      rules.push((value) => {
        if (!value) return true;
        return pattern.test(value) || useHTranslate('email_invalid');
      });
    }

    return rules;
  };

  // Phone validation rules
  // Default Swedish phone number pattern: 10 digits, optional spaces/dashes
  // Accepts formats like: 0701234567, 070-123 45 67, 070 123 45 67
  const phoneRules = (pattern?: RegExp) => {
    const swedishPhonePattern = /^(0|\+46)[\s-]?[1-9][\d\s-]{7,12}$/;
    const validationPattern = pattern || swedishPhonePattern;

    return [
      (value) => !!value || useHTranslate('phone_required'),
      (value) => validationPattern.test(value) || useHTranslate('phone_invalid'),
    ];
  };

  // Value validation rules (for settings)
  const valueRules = (isEditing = false, isRequired = true) => {
    if (isEditing && !isRequired) return [];

    return [
      (value) => {
        if (value === null || value === undefined || value === '') {
          return isRequired ? useHTranslate('value_required') : true;
        }
        return true;
      },
    ];
  };

  // Type validation rules (for settings)
  const typeRules = [
    (value) => !!value || useHTranslate('type_required'),
    (value) => {
      const validTypes = ['string', 'number', 'boolean', 'object', 'password'];
      return validTypes.includes(value) || useHTranslate('type_invalid');
    },
  ];

  // Object/JSON validation rules
  const objectValueRules = [
    (value) => {
      if (!value || value.trim() === '') return true;

      try {
        JSON.parse(value);
        return true;
      } catch (_e) {
        return useHTranslate('json_invalid');
      }
    },
  ];

  // Number validation rules
  const numberValueRules = [
    (value) => {
      if (value === null || value === undefined || value === '') return true;

      const num = Number(value);
      return !isNaN(num) || useHTranslate('number_invalid');
    },
  ];

  // Integer validation rules (Hub Joi `.integer()`, optional `.min()` / `.required()`)
  const integerRules = (options: { min?: number; max?: number; required?: boolean } = {}) => {
    const { min, max, required = false } = options;
    const rules: Array<(value: any) => string | true> = [];

    if (required) {
      rules.push((value) => {
        if (value === null || value === undefined || value === '') {
          return useHTranslate('field_required');
        }
        return true;
      });
    }

    rules.push((value) => {
      if (value === null || value === undefined || value === '') return true;

      let num: number;
      if (typeof value === 'number') {
        num = value;
      } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || !/^[+-]?\d+$/.test(trimmed)) return useHTranslate('number_invalid');
        num = Number(trimmed);
      } else {
        return useHTranslate('number_invalid');
      }

      if (!Number.isFinite(num)) return useHTranslate('number_invalid');
      if (!Number.isInteger(num)) return useHTranslate('integer_invalid');
      if (min !== undefined && num < min) return useHTranslate('number_min', { min });
      if (max !== undefined && num > max) return useHTranslate('number_max', { max });
      return true;
    });

    return rules;
  };

  // Password confirmation rules
  const passwordConfirmRules = (password: string) => [
    (value) => !!value || useHTranslate('confirm_password_required'),
    (value) => value === password || useHTranslate('passwords_must_match'),
  ];

  // Category validation rules (for settings)
  const categoryRules = [
    (value) => {
      if (!value) return true; // Optional field
      return value === 'custom' || useHTranslate('category_invalid');
    },
  ];

  // Username — API is Joi.string().required() with no charset/min
  const usernameRules = [(value) => !!value || useHTranslate('username_required')];

  // Password validation rules
  const passwordRules = (isRequired = true) => {
    if (!isRequired) return [];

    return [
      (value) => !!value || useHTranslate('password_required'),
      (value) => (value && value.length >= 8) || useHTranslate('password_min_length', { length: 8 }),
    ];
  };

  // URL validation rules - validates URL or domain format
  // Accepts both full URLs (with protocol) and plain domains
  // Auto-detects arrays and validates each item
  const urlRules = [
    (value) => {
      // Domain pattern for validation
      // eslint-disable-next-line security/detect-unsafe-regex -- quantifiers are bounded by literal '.' separator; no catastrophic backtracking on short domain strings
      const domainPattern = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      // Full URL pattern with protocol
      const urlPattern =
        // eslint-disable-next-line security/detect-unsafe-regex -- same domain sub-pattern, bounded by protocol anchor and path separator
        /^https?:\/\/(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|localhost)(?::\d{1,5})?(?:[/?#].*)?$/;

      const validateItem = (item: string): boolean => {
        if (!item || item.trim() === '') return true;
        const trimmed = item.trim();
        // Hub item/guide/form/domain URL fields are unconstrained strings.
        if (
          trimmed.startsWith('/') ||
          trimmed.startsWith('#') ||
          trimmed.startsWith('.') ||
          trimmed.startsWith('//') ||
          trimmed.startsWith('mailto:') ||
          trimmed.startsWith('tel:')
        ) {
          return true;
        }
        if (trimmed.includes('/') && !/\s/.test(trimmed)) return true;
        return urlPattern.test(trimmed) || domainPattern.test(trimmed);
      };

      // Handle array
      if (Array.isArray(value)) {
        if (value.length === 0) return true;

        for (const item of value) {
          if (!validateItem(item)) {
            return useHTranslate('url_invalid');
          }
        }
        return true;
      }

      // Handle single value
      if (!value || value.trim() === '') return true;

      if (!validateItem(value)) {
        return useHTranslate('url_invalid');
      }

      return true;
    },
  ];

  // No spaces validation rules - validates that value contains no whitespace
  const noSpacesRules = (message?: string) => [
    (value) => {
      if (!value) return true;
      return /^\S*$/.test(value) || message || useHTranslate('no_spaces');
    },
  ];

  // User settings validation rules
  const userSettingsRules = {
    defaultPageType: [
      (value) => {
        if (!value) return true; // Optional field
        const validTypes = ['workspace', 'lastworkspace', 'allworkspaces'];
        return validTypes.includes(value) || useHTranslate('default_page_type_invalid');
      },
    ],
  };

  return {
    keyRules,
    slugRules,
    stringKeyRules,
    localeKeyRules,
    minLengthRules,
    requiredRules,
    nameRules,
    idRules,
    emailRules,
    phoneRules,
    valueRules,
    typeRules,
    objectValueRules,
    numberValueRules,
    integerRules,
    passwordConfirmRules,
    categoryRules,
    usernameRules,
    passwordRules,
    urlRules,
    noSpacesRules,
    userSettingsRules,
  };
}
